import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import DvrRoundedIcon from '@material-ui/icons/DvrRounded';

const STORAGE_KEY = 'hyperblox_logs_fab_position';
const SIZE = 44; 
const EDGE_MARGIN = 8; 
const DRAG_THRESHOLD = 4; 
const POPOVER_GAP = 12; 
const HOVER_HIDE_DELAY = 160; 


const CATEGORY_ORDER = ['validator'];

const CATEGORY_META = {
  validator: { label: 'Validator', key: "Blueprint Validator", color: '#DC2626' },
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

// Default: top-right corner.
function getDefaultPosition() {
  if (typeof window === 'undefined') return { x: 24, y: 75 };
  return {
    x: window.innerWidth - SIZE - 24,
    y: 75,
  };
}

function loadStoredPosition() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
      return {
        x: clamp(parsed.x, EDGE_MARGIN, window.innerWidth - SIZE - EDGE_MARGIN),
        y: clamp(parsed.y, EDGE_MARGIN, window.innerHeight - SIZE - EDGE_MARGIN),
      };
    }
  } catch (e) {
    // corrupt or inaccessible storage — fall back to default position
  }
  return null;
}

const useStyles = makeStyles(() => ({
  wrapper: {
    position: 'fixed',
    zIndex: 1300,
    touchAction: 'none',
    userSelect: 'none',
  },
  iconButton: {
    width: SIZE,
    height: SIZE,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)',
    border: '1px solid #e2e5ea',
    boxShadow: '0 2px 10px rgba(16,24,40,0.14)',
    cursor: 'grab',
    position: 'relative',
    transition: 'box-shadow .2s ease, border-color .2s ease, transform .15s ease',
    '&:hover': {
      boxShadow: '0 6px 20px rgba(16,24,40,0.22)',
      borderColor: '#c7d2e0',
    },
    '&:active': {
      cursor: 'grabbing',
      transform: 'scale(0.96)',
    },
  },
  iconBg: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #EFF6FF 0%, #E0EAFE 100%)',
    color: '#3B82F6',
  },
  badge: {
    position: 'absolute',
    top: -4,
    left: -4,
    minWidth: 20,
    height: 20,
    padding: '0 5px',
    borderRadius: 10,
    background: '#DC2626',
    color: '#fff',
    fontSize: 11.5,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 0 2px #fff',
    lineHeight: 1,
  },
  liveDot: {
    position: 'absolute',
    bottom: 1,
    right: 1,
    width: 9,
    height: 9,
    borderRadius: '50%',
    background: '#22C55E',
    boxShadow: '0 0 0 2px #fff',
  },
  '@keyframes livePulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(34,197,94,0.55), 0 0 0 2px #fff' },
    '70%': { boxShadow: '0 0 0 6px rgba(34,197,94,0), 0 0 0 2px #fff' },
    '100%': { boxShadow: '0 0 0 0 rgba(34,197,94,0), 0 0 0 2px #fff' },
  },
  liveDotPulsing: {
    animation: '$livePulse 2s ease-out infinite',
  },
  popover: {
    position: 'fixed',
    zIndex: 1299,
    display: 'flex',
    alignItems: 'center',
    background: '#ffffff',
    borderRadius: 14,
    boxShadow: '0 10px 30px rgba(16,24,40,0.18), 0 2px 8px rgba(16,24,40,0.08)',
    padding: '12px 18px',
    gap: 16,
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity .18s cubic-bezier(.4,0,.2,1), transform .22s cubic-bezier(.34,1.4,.64,1)',
    whiteSpace: 'nowrap',
  },
  popoverVisible: {
    opacity: 1,
  },
  // Entrance direction + resting transform per placement
  placementLeft: { transform: 'translateX(8px) scale(0.96)' },
  placementRight: { transform: 'translateX(-8px) scale(0.96)' },
  placementTop: { transform: 'translateY(8px) scale(0.96)' },
  placementBottom: { transform: 'translateY(-8px) scale(0.96)' },
  placementVisible: { transform: 'translate(0, 0) scale(1)' },
  arrow: {
    position: 'absolute',
    width: 10,
    height: 10,
    background: '#ffffff',
    transform: 'rotate(45deg)',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 36,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.15,
  },
  statLabel: {
    fontSize: 11.5,
    fontWeight: 500,
    color: '#667085',
    marginTop: 2,
  },
  divider: {
    width: 1,
    alignSelf: 'stretch',
    background: '#e5e8ee',
  },
  emptyLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    color: '#2E7D32',
    padding: '2px 4px',
  },
}));

export default function LogsFab({ counts = {}, isLive = false, onClick }) {
  const classes = useStyles();
  const [position, setPosition] = useState(() => loadStoredPosition() || getDefaultPosition());
  const suppressClickRef = useRef(false);

  const [hoverIntent, setHoverIntent] = useState(false); // mouse is over the button
  const [popoverMounted, setPopoverMounted] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [placement, setPlacement] = useState('left');
  const [popoverCoords, setPopoverCoords] = useState({ left: 0, top: 0 });
  const [arrowOffset, setArrowOffset] = useState(0);

  const popoverRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const dragState = useRef({ dragging: false, moved: false, startX: 0, startY: 0, originX: 0, originY: 0 });

  const total = Object.values(CATEGORY_META).reduce((sum, meta) => sum + (counts[meta.key] || 0), 0);
  const badgeCount = counts['Blueprint Validator'] || 0
  const badgeColor = CATEGORY_META["validator"].color

  const computePlacement = useCallback(() => {
    const node = popoverRef.current;
    if (!node) return;
    const { width, height } = node.getBoundingClientRect();

    const fabCenterY = position.y + SIZE / 2;
    const fabCenterX = position.x + SIZE / 2;

    const spaceRight = window.innerWidth - (position.x + SIZE) - EDGE_MARGIN;
    const spaceLeft = position.x - EDGE_MARGIN;
    const spaceBottom = window.innerHeight - (position.y + SIZE) - EDGE_MARGIN;
    const spaceTop = position.y - EDGE_MARGIN;

    const fitsLeft = spaceLeft >= width + POPOVER_GAP;
    const fitsRight = spaceRight >= width + POPOVER_GAP;

    let nextPlacement;
    let left;
    let top;

    if (fitsLeft || fitsRight) {
      // Prefer left (matches the default top-right resting position), unless
      // right actually has more room to work with.
      nextPlacement = fitsLeft && (!fitsRight || spaceLeft >= spaceRight) ? 'left' : 'right';
      left = nextPlacement === 'left' ? position.x - POPOVER_GAP - width : position.x + SIZE + POPOVER_GAP;
      top = clamp(fabCenterY - height / 2, EDGE_MARGIN, window.innerHeight - height - EDGE_MARGIN);
    } else if (spaceTop >= height + POPOVER_GAP || spaceBottom >= height + POPOVER_GAP) {
      nextPlacement = spaceBottom >= height + POPOVER_GAP && spaceBottom >= spaceTop ? 'bottom' : 'top';
      top = nextPlacement === 'bottom' ? position.y + SIZE + POPOVER_GAP : position.y - POPOVER_GAP - height;
      left = clamp(fabCenterX - width / 2, EDGE_MARGIN, window.innerWidth - width - EDGE_MARGIN);
    } else {
      // Nothing has ideal room — best-effort clamp, still prefer left.
      nextPlacement = 'left';
      left = clamp(position.x - POPOVER_GAP - width, EDGE_MARGIN, window.innerWidth - width - EDGE_MARGIN);
      top = clamp(fabCenterY - height / 2, EDGE_MARGIN, window.innerHeight - height - EDGE_MARGIN);
    }

    // Arrow position: point at the fab's center, clamped to stay inside the bubble.
    let offset;
    if (nextPlacement === 'left' || nextPlacement === 'right') {
      offset = clamp(fabCenterY - top, 16, height - 16);
    } else {
      offset = clamp(fabCenterX - left, 16, width - 16);
    }

    setPlacement(nextPlacement);
    setPopoverCoords({ left, top });
    setArrowOffset(offset);
  }, [position]);

  useLayoutEffect(() => {
    if (!popoverMounted) return undefined;
    computePlacement();
    const raf = requestAnimationFrame(() => setPopoverVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [popoverMounted, computePlacement]);

  // Re-measure if the viewport changes while the preview is open.
  useEffect(() => {
    if (!popoverMounted) return undefined;
    function handleViewportChange() {
      computePlacement();
    }
    window.addEventListener('resize', handleViewportChange);
    return () => window.removeEventListener('resize', handleViewportChange);
  }, [popoverMounted, computePlacement]);

  const openPreview = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setHoverIntent(true);
    setPopoverMounted(true);
  }, []);

  const closePreview = useCallback(() => {
    setHoverIntent(false);
    setPopoverVisible(false);
    hideTimeoutRef.current = setTimeout(() => {
      setPopoverMounted(false);
    }, HOVER_HIDE_DELAY);
  }, []);

  // ---- Drag handling (unchanged behavior: press-drag-release, click only fires
  // if the pointer never moved past the threshold) ----
  const handlePointerMove = useCallback((e) => {
    if (!dragState.current.dragging) return;
    const point = e.touches ? e.touches[0] : e;
    const dx = point.clientX - dragState.current.startX;
    const dy = point.clientY - dragState.current.startY;
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      dragState.current.moved = true;
    }
    const nextX = clamp(dragState.current.originX + dx, EDGE_MARGIN, window.innerWidth - SIZE - EDGE_MARGIN);
    const nextY = clamp(dragState.current.originY + dy, EDGE_MARGIN, window.innerHeight - SIZE - EDGE_MARGIN);
    setPosition({ x: nextX, y: nextY });
    if (e.cancelable) e.preventDefault();
  }, []);

  const handlePointerUp = useCallback(() => {
    const { moved } = dragState.current;
    dragState.current.dragging = false;
    window.removeEventListener('mousemove', handlePointerMove);
    window.removeEventListener('mouseup', handlePointerUp);
    window.removeEventListener('touchmove', handlePointerMove);
    window.removeEventListener('touchend', handlePointerUp);

    setPosition((current) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
      } catch (e) {
        // storage unavailable (e.g. private browsing) — position just won't persist
      }
      return current;
    });

    if(moved){
      suppressClickRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handlePointerMove, onClick]);

  const handlePointerDown = useCallback((e) => {
    // Dragging invalidates any open preview — close it immediately.
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setPopoverVisible(false);
    setPopoverMounted(false);
    setHoverIntent(false);

    const point = e.touches ? e.touches[0] : e;
    dragState.current = {
      dragging: true,
      moved: false,
      startX: point.clientX,
      startY: point.clientY,
      originX: position.x,
      originY: position.y,
    };
    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('touchend', handlePointerUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, handlePointerMove, handlePointerUp]);

  // Keep the button on-screen if the window is resized after a position was saved
  useEffect(() => {
    function handleResize() {
      setPosition((prev) => ({
        x: clamp(prev.x, EDGE_MARGIN, window.innerWidth - SIZE - EDGE_MARGIN),
        y: clamp(prev.y, EDGE_MARGIN, window.innerHeight - SIZE - EDGE_MARGIN),
      }));
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clean up any dangling window/timeout handles if unmounted mid-drag or mid-hover
  useEffect(() => () => {
    window.removeEventListener('mousemove', handlePointerMove);
    window.removeEventListener('mouseup', handlePointerUp);
    window.removeEventListener('touchmove', handlePointerMove);
    window.removeEventListener('touchend', handlePointerUp);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
  }, [handlePointerMove, handlePointerUp]);

  const placementClass = {
    left: classes.placementLeft,
    right: classes.placementRight,
    top: classes.placementTop,
    bottom: classes.placementBottom,
  }[placement];

  const arrowStyle =
    placement === 'left'
      ? { right: -5, top: arrowOffset - 5, boxShadow: '3px -3px 4px rgba(16,24,40,0.03)' }
      : placement === 'right'
      ? { left: -5, top: arrowOffset - 5, boxShadow: '-3px 3px 4px rgba(16,24,40,0.03)' }
      : placement === 'top'
      ? { bottom: -5, left: arrowOffset - 5, boxShadow: '3px 3px 4px rgba(16,24,40,0.03)' }
      : { top: -5, left: arrowOffset - 5, boxShadow: '-3px -3px 4px rgba(16,24,40,0.03)' };

    

  return (
    <>
      <div
        className={classes.wrapper}
        id="logs-fab"
        style={{ left: position.x, top: position.y }}
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        onMouseEnter={openPreview}
        onMouseLeave={closePreview}
        onClick={(e) => {
          if(suppressClickRef.current){
            suppressClickRef.current = false
          }else{
            onClick(e)
          }
        }}
      >
        <div className={classes.iconButton} role="button" tabIndex={0} aria-label="Open logs">
          <div className={classes.iconBg}>
            <DvrRoundedIcon style={{ fontSize: 18 }} />
          </div>
          {badgeCount > 0 && (
            <span className={classes.badge} id="blueprint-validation-count-badge" style={{ background: badgeColor }}>
              {badgeCount}
            </span>
          )}
          {isLive && (
            <span className={`${classes.liveDot} ${classes.liveDotPulsing}`} />
          )}
        </div>
      </div>

      {popoverMounted && (
        <div
          ref={popoverRef}
          className={`${classes.popover} ${placementClass} ${
            popoverVisible ? `${classes.popoverVisible} ${classes.placementVisible}` : ''
          }`}
          style={{ left: popoverCoords.left, top: popoverCoords.top }}
        >
          <span className={classes.arrow} style={arrowStyle} />
          {total > 0 ? (
            CATEGORY_ORDER.map((key, i) => {
              const meta = CATEGORY_META[key];
              return (
                <React.Fragment key={key}>
                  <div className={classes.statItem}>
                    <div className={classes.statValue} id="blueprint-validation-count" style={{ color: meta.color }}>
                      {counts[meta.key] || 0}
                    </div>
                    <div className={classes.statLabel}>{meta.label}</div>
                  </div>
                  {i < CATEGORY_ORDER.length - 1 && <div className={classes.divider} />}
                </React.Fragment>
              );
            })
          ) : (
            <span className={classes.emptyLabel}>No validation issues detected</span>
          )}
        </div>
      )}
    </>
  );
}